import { NgForOf, NgIf, NgStyle } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TopLayerDirective } from '../../directives/top-layer.directive';
import { IMessage, MessageLevel } from '../message-notification.service';

@Component({
  selector: 'app-alert-banner',
  templateUrl: './alert-banner.component.html',
  imports: [
    NgIf,
    NgForOf,
    NgStyle,
    TopLayerDirective
  ],
  styleUrls: ['./alert-banner.component.scss']
})

export class AlertBannerComponent {
  private static readonly TOAST_TIMEOUT_MS = 12500;
  private static readonly MESSAGE_HEIGHT_PX = 46;
  private static readonly MESSAGE_AND_DETAILS_HEIGHT_PX = 90;
  visible = false;
  messages: any[] = [];
  private readonly updateUi: (update) => void;
  @ViewChild('scrollToMe') private scrollToMe: ElementRef;

  constructor(private ngZone: NgZone, private router: Router, private dialog: MatDialog) {
    this.updateUi = this.updateUiInZone;
  }

  private static parseDetails(details?: any | undefined): string | undefined {
    if (!details)
      return undefined;

    if (typeof details === 'string')
      return details;

    if (details instanceof Array) {
      const strings = details.every(item => typeof item === 'string');
      return strings ? details.join('<br>') : undefined;
    }

    if (details instanceof HttpErrorResponse) {
      if (details.message?.length > 0) {
        let msg = details.message;
        if (details.error?.message)
          msg += ` (${ details.error.message })`;
        if (details.error?.Message)
          msg += ` (${ details.error.Message })`;
        return msg;
      }

      return `${ details.status }: ${ details.statusText }`;
    }

    if (details instanceof Error)
      return details.message;

    return undefined;
  }

  private static getDateTime(): string {
    const d = new Date();
    const date = ('' + d.getUTCDate()).padStart(2, '0');
    const month = ('' + (d.getUTCMonth() + 1)).padStart(2, '0');
    const year = ('' + d.getUTCFullYear());
    const hour = ('' + d.getUTCHours()).padStart(2, '0');
    const minute = ('' + d.getUTCMinutes()).padStart(2, '0');
    const second = ('' + d.getUTCSeconds()).padStart(2, '0');
    return `${ year }-${ month }-${ date } ${ hour }:${ minute }:${ second } UTC`;
  }

  show(message: IMessage): void {
      //using unshift so that the newest banners so above the older ones.
    this.messages.unshift({
      id: message,
      message: message.message,
      details: AlertBannerComponent.parseDetails(message.details),
      reference: message.reference?.substring(0, 8),
      theme: message.level.toString().toLowerCase(),
      time: AlertBannerComponent.getDateTime()
    });

    this.updateTops();

    this.updateUi(() => {
      this.visible = true;
    });

    if (message.level !== MessageLevel.ERROR) {
      setTimeout(() => {
        this.hide(message);
      }, AlertBannerComponent.TOAST_TIMEOUT_MS);
    }
  }

  hide(message: IMessage): void {
    const index = this.messages.findIndex(m => m.id === message);
    if (index > -1) {
      this.messages.splice(index, 1);
      this.updateTops();
      this.updateUi(() => {
        this.visible = this.messages.length !== 0;
      });
    }
  }

  onDetailsClick(event: MouseEvent, msg: any): void {
    if (event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    const anchor = (event.target as HTMLElement)?.closest('a');
    const href = anchor?.getAttribute('href');
    if (!href || !href.startsWith('/')) {
      return;
    }
    event.preventDefault();
    this.hide(msg.id);
    this.dialog.closeAll();
    this.router.navigateByUrl(href);
  }

  copyToClipboard(msg: any): void {
    const listener = (e: ClipboardEvent) => {
      const data = JSON.stringify({ time: msg.time, details: msg.id }, null, 2);
      e.clipboardData.setData('text/plain', data);
      e.preventDefault();
      document.removeEventListener('copy', listener);
      this.updateUi(() => msg.copied = true);
      setTimeout(() => {
        this.updateUi(() => msg.copied = false);
      }, 2000);
    };
    document.addEventListener('copy', listener);
    document.execCommand('copy');
  }

  private updateTops(): void {
    let top = 10;
    this.messages.forEach((m, i) => {
      m.top = top + 'px';
      top += m.details ? AlertBannerComponent.MESSAGE_AND_DETAILS_HEIGHT_PX : AlertBannerComponent.MESSAGE_HEIGHT_PX;
    });
  }

  private updateUiInZone(update: () => void): void {
    this.ngZone.run(() => update());
  }
}
